import type { Map as MapboxMap } from 'mapbox-gl'

/**
 * Prefix for the per-colour diagonal-hatch images used to render pattern
 * (non-solid) DXF HATCH areas. Image ids look like `hatch-#808080`.
 *
 * Mapbox has no native line-hatch fill, so pattern hatches are painted with a
 * `fill-pattern` referencing one of these images. The images are generated
 * lazily on the map's `styleimagemissing` event (see registerHatchImageHandler),
 * which works for both GeoJSON and vector-tile sources.
 */
export const HATCH_IMAGE_PREFIX = 'hatch-'

// Build a small, seamlessly-tiling 45° diagonal hatch tile in the given colour.
const makeHatchImage = (color: string): ImageData => {
  const dpr = 2
  const size = 10 * dpr // 10px logical tile — sparse, fine hatch
  const lw = Math.max(1, Math.round(dpr * 0.75)) // thin lines
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  ctx.strokeStyle = color
  ctx.lineWidth = lw
  // Seamless 45° diagonal: the anti-diagonal plus the two opposite corners.
  ctx.beginPath()
  ctx.moveTo(0, size)
  ctx.lineTo(size, 0)
  ctx.moveTo(-lw, lw)
  ctx.lineTo(lw, -lw)
  ctx.moveTo(size - lw, size + lw)
  ctx.lineTo(size + lw, size - lw)
  ctx.stroke()
  return ctx.getImageData(0, 0, size, size)
}

/**
 * Registers a `styleimagemissing` handler that generates per-colour hatch
 * images on demand. Safe to call on every map load — the `hasImage` guard
 * de-dupes, and missing images are (re)generated automatically after a style
 * reload.
 */
export const registerHatchImageHandler = (map: MapboxMap) => {
  map.on('styleimagemissing', (e: { id: string }) => {
    const id = e.id
    if (
      typeof id !== 'string' ||
      !id.startsWith(HATCH_IMAGE_PREFIX) ||
      map.hasImage(id)
    ) {
      return
    }
    const color = id.slice(HATCH_IMAGE_PREFIX.length)
    map.addImage(id, makeHatchImage(color), { pixelRatio: 2 })
  })
}
